// Aquila Air & Heating — document templates for the Command Center Forms builder.

const DEFAULT_TERMS =
  "This agreement is between Aquila Air & Heating and the customer named above. " +
  "Prices are valid for 30 days. Work will be performed in a professional manner per Florida code. " +
  "Payment is due upon completion unless financing terms are arranged in advance. " +
  "By signing below, the customer approves the scope of work and pricing described in this document.";

export const TEMPLATES = [
  {
    id: "install",
    name: "Installation Agreement",
    short: "Installation Agreement",
    blurb: "Full installation contract with equipment, pricing & Lifetime Warranty.",
    sections: [
      {
        title: "Customer & Job Information",
        fields: [
          { key: "owner_name", label: "Homeowner Name", type: "text" },
          { key: "install_date", label: "Install Date", type: "date" },
          { key: "service_address", label: "Service Address", type: "text" },
          { key: "city", label: "City", type: "text" },
          { key: "zip", label: "ZIP", type: "text" },
          { key: "phone", label: "Phone", type: "text" },
          { key: "email", label: "Email", type: "text" },
        ],
      },
      {
        title: "Equipment & Scope of Work",
        fields: [
          { key: "equipment_description", label: "Equipment / System Description", type: "textarea" },
          { key: "line_items", label: "Line Items", type: "lineitems" },
          { key: "tax_rate", label: "Tax Rate (%)", type: "number", default: 0 },
        ],
        computed: "invoice",
      },
      {
        title: "Lifetime Warranty",
        intro:
          "Aquila Air & Heating includes its Lifetime Warranty on qualifying installations. " +
          "Coverage and requirements are provided with this agreement.",
        fields: [
          { key: "lifetime_warranty_included", label: "Lifetime Warranty", type: "dropdown", options: ["Included", "Not Included"] },
          { key: "warranty_ack", label: "Customer acknowledges receipt and understanding of the Lifetime Warranty terms and requirements.", type: "checkbox" },
          { key: "warranty_status", label: "Warranty Registration / Status", type: "dropdown", options: ["Pending", "Submitted", "Active"] },
          { key: "warranty_notes", label: "Warranty Notes / Confirmation #", type: "textarea" },
        ],
      },
      { title: "Our Guarantees", guarantees: true, fields: [] },
      {
        title: "Terms & Authorization",
        fields: [
          { key: "terms", label: "Terms", type: "textarea", default: DEFAULT_TERMS },
          { key: "owner_sign_name", label: "Signer Name", type: "text" },
          { key: "owner_sign_date", label: "Date", type: "date" },
        ],
      },
    ],
  },
  {
    id: "invoice",
    name: "Invoice",
    short: "Invoice",
    blurb: "Itemized invoice with totals and online payment link.",
    sections: [
      {
        title: "Bill To",
        fields: [
          { key: "bill_to_name", label: "Customer Name", type: "text" },
          { key: "invoice_no", label: "Invoice #", type: "text" },
          { key: "bill_to_address", label: "Address", type: "text" },
          { key: "invoice_date", label: "Invoice Date", type: "date" },
          { key: "bill_to_city", label: "City", type: "text" },
          { key: "due_date", label: "Due Date", type: "date" },
          { key: "bill_to_zip", label: "ZIP", type: "text" },
          { key: "bill_to_phone", label: "Phone", type: "text" },
          { key: "bill_to_email", label: "Email", type: "text" },
        ],
      },
      {
        title: "Line Items",
        fields: [
          { key: "line_items", label: "Line Items", type: "lineitems" },
          { key: "tax_rate", label: "Tax Rate (%)", type: "number", default: 0 },
        ],
        computed: "invoice",
      },
      {
        title: "Notes",
        fields: [{ key: "notes", label: "Notes", type: "textarea" }],
      },
      {
        title: "Signature",
        fields: [
          { key: "owner_sign_name", label: "Signer Name", type: "text" },
          { key: "owner_sign_date", label: "Date", type: "date" },
        ],
      },
    ],
  },
  {
    id: "estimate",
    name: "Estimate / Proposal",
    short: "Estimate",
    blurb: "Professional proposal with scope, pricing & guarantees.",
    sections: [
      {
        title: "Prepared For",
        fields: [
          { key: "customer_name", label: "Customer Name", type: "text" },
          { key: "estimate_no", label: "Estimate #", type: "text" },
          { key: "service_address", label: "Address", type: "text" },
          { key: "estimate_date", label: "Date", type: "date" },
          { key: "city", label: "City", type: "text" },
          { key: "valid_until", label: "Valid Until", type: "date" },
          { key: "zip", label: "ZIP", type: "text" },
          { key: "phone", label: "Phone", type: "text" },
          { key: "email", label: "Email", type: "text" },
        ],
      },
      {
        title: "Scope of Work",
        fields: [
          { key: "scope", label: "Scope Description", type: "textarea" },
          { key: "line_items", label: "Line Items", type: "lineitems" },
          { key: "tax_rate", label: "Tax Rate (%)", type: "number", default: 0 },
        ],
        computed: "invoice",
      },
      { title: "Our Guarantees", guarantees: true, fields: [] },
      {
        title: "Signature",
        fields: [
          { key: "owner_sign_name", label: "Signer Name", type: "text" },
          { key: "owner_sign_date", label: "Date", type: "date" },
        ],
      },
    ],
  },
  {
    id: "change_order",
    name: "Change Order",
    short: "Change Order",
    blurb: "Document additional work and recompute the contract balance.",
    sections: [
      {
        title: "Project Information",
        fields: [
          { key: "customer_name", label: "Customer Name", type: "text" },
          { key: "change_order_no", label: "Change Order #", type: "text" },
          { key: "service_address", label: "Service Address", type: "text" },
          { key: "change_date", label: "Date", type: "date" },
          { key: "original_contract_price", label: "Original Contract Price", type: "money" },
          { key: "previously_paid", label: "Previously Paid", type: "money" },
        ],
      },
      {
        title: "Additional Work / Changes",
        fields: [{ key: "change_items", label: "Change Items", type: "change_items" }],
      },
      { title: "Revised Balance", computed: "change_total", fields: [] },
      {
        title: "Authorization",
        fields: [
          { key: "owner_sign_name", label: "Signer Name", type: "text" },
          { key: "owner_sign_date", label: "Date", type: "date" },
        ],
      },
    ],
  },
];

export const getTemplate = (id) => TEMPLATES.find((t) => t.id === id) || TEMPLATES[0];

// Ensures sections/fields arrays exist and applies defaults metadata. Pass-through expansion.
export const expandTemplateFields = (template) => {
  if (!template) return { sections: [] };
  return {
    ...template,
    sections: (template.sections || []).map((s) => ({ ...s, fields: s.fields || [] })),
  };
};

// No legacy transformation needed for Launch — return a shallow copy.
export const normalizeLegacyData = (data) => ({ ...(data || {}) });

// Parse "Cocoa, FL 32922" style addresses into { city, zip }.
export const parseCityZip = (address = "") => {
  const out = { city: "", zip: "" };
  if (!address) return out;
  const zipMatch = address.match(/\b(\d{5})(?:-\d{4})?\b/);
  if (zipMatch) out.zip = zipMatch[1];
  const cityMatch = address.match(/,\s*([A-Za-z .'-]+?)\s*,?\s*[A-Z]{2}\b/);
  if (cityMatch) out.city = cityMatch[1].trim();
  return out;
};
